var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from "react";
import { Drawer, Box, Typography, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme, CircularProgress, } from "@mui/material";
import { Add, CloseCircle, Edit } from "iconsax-react";
import { Icon } from "@iconify/react";
import { useGetCostComponentsQuery, useCreateCostComponentMutation, useUpdateCostComponentMutation, useDeleteCostComponentMutation, } from "../../../features/costComponents/costComponentsApi";
import { useAuth } from "../../../auth/useAuth";
import { useSnackbar } from "notistack";
export const MaterialBottomDrawer = ({ open, onClose, title, companyId, externalId, configMonth, projectNumber, childProjectNumber, }) => {
    const theme = useTheme();
    const { account } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = (account === null || account === void 0 ? void 0 : account.username) || "Unknown User";
    const { data: costComponents = [], isLoading } = useGetCostComponentsQuery({ companyId, externalId, configMonth, projectNumber, childProjectNumber }, {
        skip: !open || !companyId || !externalId || !configMonth || !projectNumber,
        refetchOnMountOrArgChange: true,
    });
    const [createCostComponent] = useCreateCostComponentMutation();
    const [updateCostComponent] = useUpdateCostComponentMutation();
    const [deleteCostComponent] = useDeleteCostComponentMutation();
    const items = costComponents.filter((c) => c.isActive !== false);
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [newPrice, setNewPrice] = useState("0");
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editPrice, setEditPrice] = useState("");
    useEffect(() => {
        if (open) {
            setIsAdding(false);
            setEditingId(null);
        }
    }, [open]);
    const handleStartAdd = () => {
        setIsAdding(true);
        setNewName("");
        setNewPrice("0");
    };
    const handleConfirmAdd = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!newName.trim()) {
            enqueueSnackbar("Vul een naam in", {
                variant: "warning",
            });
            return;
        }
        try {
            yield createCostComponent({
                externalId,
                name: newName.trim(),
                price: parseFloat(newPrice) || 0,
                companyId,
                configMonth,
                projectNumber,
                childProjectNumber,
                createdBy: currentUser,
            }).unwrap();
            setIsAdding(false);
            setNewName("");
            setNewPrice("");
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het toevoegen van kostenposten", {
                variant: "error",
            });
        }
    });
    const handleCancelAdd = () => {
        setIsAdding(false);
        setNewName("");
        setNewPrice("");
    };
    const handleStartEdit = (item) => {
        setEditingId(item.id);
        setEditName(item.name);
        setEditPrice(String(item.price));
    };
    const handleConfirmEdit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!editingId)
            return;
        const item = items.find((i) => i.id === editingId);
        if (!item)
            return;
        if (!editName.trim()) {
            enqueueSnackbar("Vul een naam in", {
                variant: "warning",
            });
            return;
        }
        try {
            yield updateCostComponent({
                id: editingId,
                externalId: item.externalId,
                name: editName,
                price: parseFloat(editPrice) || 0,
                isActive: true,
                configMonth,
                projectNumber,
                childProjectNumber,
                modifiedBy: currentUser,
            }).unwrap();
            setEditingId(null);
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het bijwerken van kostenposten", {
                variant: "error",
            });
        }
    });
    const handleCancelEdit = () => {
        setEditingId(null);
    };
    const handleDelete = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield deleteCostComponent({ id, modifiedBy: currentUser }).unwrap();
            if (editingId === id)
                setEditingId(null);
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het verwijderen van kostenposten", {
                variant: "error",
            });
        }
    });
    return (React.createElement(Drawer, { anchor: "bottom", open: open, onClose: onClose, sx: {
            "& .MuiDrawer-paper": {
                height: "70vh",
                maxHeight: 560,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                backgroundColor: "background.paper",
            },
        } },
        React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
            } },
            React.createElement(Box, { sx: {
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: "grey.400",
                    mx: "auto",
                    mt: 1,
                    flexShrink: 0,
                } }),
            React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    pt: 2,
                    pb: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                } },
                React.createElement(Typography, { variant: "h6", fontWeight: 600 }, title),
                React.createElement(IconButton, { onClick: onClose, size: "small", "aria-label": "Sluiten" },
                    React.createElement(Icon, { icon: "mingcute:close-line", width: 22, height: 22 }))),
            React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                } },
                React.createElement(Typography, { variant: "body2", color: "text.secondary" },
                    items.length,
                    " Kostenposten"),
                React.createElement(IconButton, { onClick: handleStartAdd, sx: {
                        width: 40,
                        height: 40,
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                    }, "aria-label": "Toevoegen" },
                    React.createElement(Add, { size: 20 }))),
            React.createElement(TableContainer, { sx: { flex: 1, overflow: "auto", px: 2 } },
                React.createElement(Table, { size: "small", stickyHeader: true, sx: {
                        "& tbody .MuiTableCell-root": { borderTop: "none" },
                    } },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: {
                                    color: "#1B6EA3",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "24px",
                                    letterSpacing: 0,
                                    background: "background.paper",
                                    borderBottom: 1,
                                    borderColor: "divider",
                                } }, "Kostenpost"),
                            React.createElement(TableCell, { align: "right", sx: {
                                    color: "#1B6EA3",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    lineHeight: "24px",
                                    letterSpacing: 0,
                                    background: "background.paper",
                                    borderBottom: 1,
                                    borderColor: "divider",
                                } }, "Prijs"),
                            React.createElement(TableCell, { align: "right", sx: {
                                    width: 100,
                                    background: "background.paper",
                                    borderBottom: 1,
                                    borderColor: "divider",
                                } }))),
                    React.createElement(TableBody, null,
                        isAdding && (React.createElement(TableRow, { sx: { backgroundColor: "action.hover" } },
                            React.createElement(TableCell, null,
                                React.createElement(TextField, { size: "small", placeholder: "Nieuw Kostenpost", value: newName, onChange: (e) => setNewName(e.target.value), sx: {
                                        minWidth: 140,
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "grey.200",
                                            borderRadius: "20px",
                                        },
                                    } })),
                            React.createElement(TableCell, { align: "right" },
                                React.createElement(TextField, { size: "small", placeholder: "0", value: newPrice, onChange: (e) => {
                                        const v = e.target.value;
                                        if (v === "" || v === "-" || /^-?\d*\.?\d*$/.test(v)) {
                                            setNewPrice(v);
                                        }
                                    }, sx: {
                                        width: 80,
                                        "& .MuiInputBase-input": { textAlign: "right" },
                                    } })),
                            React.createElement(TableCell, { align: "right" },
                                React.createElement(Box, { display: "flex", gap: 1 },
                                    React.createElement(IconButton, { size: "small", onClick: handleConfirmAdd, sx: { color: "success.main" }, "aria-label": "Bevestigen" },
                                        React.createElement(Icon, { icon: "carbon:checkmark-filled" })),
                                    React.createElement(IconButton, { size: "small", onClick: handleCancelAdd, sx: { color: "error.light" }, "aria-label": "Annuleren" },
                                        React.createElement(CloseCircle, { variant: "Bulk" })))))),
                        isLoading && (React.createElement(TableRow, null,
                            React.createElement(TableCell, { colSpan: 3, align: "center", sx: { py: 4 } },
                                React.createElement(CircularProgress, { size: 32 })))),
                        !isLoading && items.length === 0 && !isAdding && (React.createElement(TableRow, null,
                            React.createElement(TableCell, { colSpan: 3, align: "center", sx: { py: 4 } },
                                React.createElement(Typography, { variant: "body2", color: "text.secondary" }, "Geen kostenposten gevonden")))),
                        items.map((item) => (React.createElement(TableRow, { key: item.id },
                            React.createElement(TableCell, null, editingId === item.id ? (React.createElement(TextField, { size: "small", value: editName, onChange: (e) => setEditName(e.target.value), sx: {
                                    minWidth: 120,
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "20px",
                                    },
                                } })) : (item.name)),
                            React.createElement(TableCell, { align: "right" }, editingId === item.id ? (React.createElement(TextField, { size: "small", placeholder: "0", value: editPrice, onChange: (e) => {
                                    const v = e.target.value;
                                    if (v === "" ||
                                        v === "-" ||
                                        /^-?\d*\.?\d*$/.test(v)) {
                                        setEditPrice(v);
                                    }
                                }, sx: {
                                    width: 80,
                                    "& .MuiInputBase-input": { textAlign: "right" },
                                } })) : (item.price)),
                            React.createElement(TableCell, { align: "right" }, editingId === item.id ? (React.createElement(Box, { display: "flex", gap: 1 },
                                React.createElement(IconButton, { size: "small", onClick: handleConfirmEdit, sx: { color: "success.main" }, "aria-label": "Opslaan" },
                                    React.createElement(Icon, { icon: "carbon:checkmark-filled" })),
                                React.createElement(IconButton, { size: "small", onClick: handleCancelEdit, sx: { color: "error.main" }, "aria-label": "Annuleren" },
                                    React.createElement(CloseCircle, { variant: "Bulk" })))) : (React.createElement(Box, { display: "flex", gap: 1 },
                                React.createElement(IconButton, { size: "small", onClick: () => handleStartEdit(item), sx: { color: "primary.main" }, "aria-label": "Bewerken" },
                                    React.createElement(Edit, { variant: "Bulk" })),
                                React.createElement(IconButton, { size: "small", onClick: () => handleDelete(item.id), sx: { color: "error.main" }, "aria-label": "Verwijderen" },
                                    React.createElement(Icon, { icon: "ic:baseline-delete", width: "24", height: "24" }))))))))))))));
};
